import { FlexBox } from '@dixa/lib-shared-habla';
import { ConversationCustomAttributesUpdatedEntry } from 'lib-shared-legacy-types';

import { CustomFieldAttributeValue } from 'apis/customFields';
import { CustomFieldChip } from 'components/customFields';
import { useCustomFields } from 'utils/customFields';

import { getLogEntryAuthorName, LogEntry } from './logEntry';
import { UserLink } from './userLink/userLink';

type ConversationCustomAttributesUpdatedProps = {
  entry: ConversationCustomAttributesUpdatedEntry;
};

export const ConversationCustomAttributesUpdated = ({
  entry,
}: ConversationCustomAttributesUpdatedProps): JSX.Element => {
  const { customFields } = useCustomFields({
    entityId: 'conversation',
    entityInstanceId: entry.csid.toString(),
    includeDeactivated: true,
    includeArchived: true,
  });

  const mapCustomAttributeValue = (
    value: string | string[],
  ): CustomFieldAttributeValue =>
    Array.isArray(value)
      ? { value, data_type: 'string_list' }
      : { value, data_type: 'string' };

  const authorName = getLogEntryAuthorName(entry);
  const authorLink = authorName ? (
    <UserLink userId={entry.author.id}>{authorName}</UserLink>
  ) : null;

  // Get deleted custom fields (in updated_attribute_ids but without values)
  const deletedCustomFields = customFields.filter((cf) => {
    const isDeleted = !entry.updated_attribute_values?.[cf.attribute.id];
    const wasUpdated = entry.updated_attribute_ids.includes(cf.attribute.id);
    return isDeleted && wasUpdated;
  });

  // Get updated custom fields (with new values)
  const updatedCustomFields = customFields.filter((cf) => {
    return entry.updated_attribute_values?.[cf.attribute.id] !== undefined;
  });

  return (
    <LogEntry
      entry={entry}
      text={
        <FlexBox flexDirection="row" gap="100" alignItems="center">
          {authorLink}
          {updatedCustomFields.length > 0 && (
            <>
              <span>set</span>
              {updatedCustomFields.map((cf) => (
                <CustomFieldChip
                  key={cf.attribute.id}
                  customField={{
                    attribute: cf.attribute,
                    value: mapCustomAttributeValue(
                      entry.updated_attribute_values![cf.attribute.id],
                    ),
                  }}
                />
              ))}
            </>
          )}
          {deletedCustomFields.length > 0 && (
            <>
              <span>
                {updatedCustomFields.length > 0 ? 'and deleted' : 'deleted'}
              </span>
              {deletedCustomFields.map((cf) => (
                <CustomFieldChip
                  key={cf.attribute.id}
                  customField={{
                    attribute: cf.attribute,
                    value: undefined,
                  }}
                />
              ))}
            </>
          )}
        </FlexBox>
      }
    />
  );
};
